/** @jsxImportSource @emotion/react */

import {
  Header,
  useSession,
  TruncatedString,
} from '@quickbit/backoffice-core-frontend';
import {
  Card,
  Typography,
  useTheme,
  DateInput,
  Table,
  Icon,
  Pagination,
  UtilityButton,
  SearchInput,
  Copy,
  UtilitySelect,
  Option,
} from '@quickbit/qb-design-system';
import _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import { useMerchantOrdersApi } from 'api/useMerchantOrdersApi';
import { QBLoader, MerchantSelect } from 'components';
import { PlatformSelect } from 'components/PlatformSelect';
import {
  CreateProcessingOrders,
  ExportCsv,
  OrderDetails,
  RetryActions,
} from './components';
import { SortOptions, OrderMinimal, OrderStatus, OrderType } from './types';
const PAGE_SIZE = 16;

type CellKey =
  | 'createdAt'
  | 'completedAt'
  | 'orderId'
  | 'amount'
  | 'status'
  | 'merchantName'
  | 'details';

const getHeaders = (): {
  label: string;
  cellKey: CellKey;
  sort?: boolean;
}[] => [
  { label: 'Created At', cellKey: 'createdAt' },
  { label: 'Completed At', cellKey: 'completedAt' },
  { label: 'Order ID', cellKey: 'orderId', sort: false },
  { label: 'Amount', cellKey: 'amount' },
  { label: 'Merchant', cellKey: 'merchantName', sort: false },
  { label: 'Status', cellKey: 'status', sort: false },
  { label: 'Details', cellKey: 'details', sort: false },
];

const CELL_KEY_TO_SORT_FIELD: Record<CellKey, SortOptions['field']> = {
  createdAt: 'CREATED_AT',
  completedAt: 'COMPLETED_AT',
  orderId: 'ORDER_ID',
  amount: 'AMOUNT',
  status: 'STATUS',
  merchantName: 'STATUS',
  details: 'STATUS',
};

const STATUS_FILTER_OPTIONS = [
  'INITIATED',
  'ADDRESS_CREATED',
  'BROKER_SENT',
  'BUY_INITIATED',
  'BUY_IN_PROGRESS',
  'BUY_COMPLETE',
  'BUY_FAILED',
  'PROCESSING_INTERNAL',
  'PROCESSING_EXTERNAL',
  'INVALID_AMOUNT',
  'REFUND_CRYPTO_RECEIVED',
  'REFUNDING',
  'REFUNDED',
  'MANUAL_REFUND_INITIATED',
  'MANUAL_REFUND_IN_PROGRESS',
  'PARTIALLY_REFUNDED',
  'MANUALLY_REFUNDED',
  'COMPLETE',
  'CRYPTO_RECEIVED',
  'CRYPTO_SOLD',
  'CANCELLED',
].map((s) => ({
  value: s,
  label: s,
}));
const ORDER_TYPE_FILTER_OPTIONS = ['PAY', 'ONRAMP', 'PROCESSING'].map((s) => ({
  value: s,
  label: _.capitalize(s),
}));
const ORDER_ACTIVITY_FILTER_OPTIONS = ['Active', 'Inactive'].map((s) => ({
  value: s,
  label: s,
}));
const ERROR_FILTER_OPTIONS = ['With errors', 'With no errors'].map((s) => ({
  value: s,
  label: s,
}));
const ORDER_ABANDONED_FILTER_OPTIONS = ['Is abandoned'].map((s) => ({
  value: s,
  label: s,
}));
const HAS_FAILED_CALLBACK_OPTIONS = ['With failed callback'].map((s) => ({
  value: s,
  label: s,
}));
export interface MerchantOrdersUiFilters {
  page: number;
  dateFilterRange: [Date | null, Date | null];
  orderType: Option | null;
  active: Option | null;
  statusFilter: Option[];
  sortOptions?: SortOptions;
  searchInput: string;
  searchByReferenceIdInput: string;
  searchByUserIdInput: string;
  searchByNexusTxCodeInput: string;
  searchQuery: string;
  searchByReferenceIdQuery: string;
  searchByUserIdQuery: string;
  searchByNexusTxCodeQuery: string;
  merchant: Option[];
  errorFilter: Option | null;
  abandonedAfterBuy: Option | null;
  hasFailedCallback: Option | null;
  platform: Option | null;
}

const DEFAULT_CREATED_FROM = new Date();
DEFAULT_CREATED_FROM.setDate(DEFAULT_CREATED_FROM.getDate() - 2);

const initialFilterState: MerchantOrdersUiFilters = {
  page: 1,
  dateFilterRange: [DEFAULT_CREATED_FROM, null],
  orderType: null,
  active: null,
  statusFilter: [],
  sortOptions: undefined,
  searchInput: '',
  searchByReferenceIdInput: '',
  searchByUserIdInput: '',
  searchByNexusTxCodeInput: '',
  searchQuery: '',
  searchByReferenceIdQuery: '',
  searchByUserIdQuery: '',
  searchByNexusTxCodeQuery: '',
  merchant: [],
  errorFilter: null,
  abandonedAfterBuy: null,
  hasFailedCallback: null,
  platform: null,
};

const getInitialFilterState = (
  params: URLSearchParams
): MerchantOrdersUiFilters => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const paramDateFilterRange = params.get('dateFilterRange')
    ? JSON.parse(params.get('dateFilterRange') as string)
    : initialFilterState.dateFilterRange;
  return {
    page: Number(params.get('page')) || 1,
    dateFilterRange: paramDateFilterRange
      ? [
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          paramDateFilterRange[0] ? new Date(paramDateFilterRange[0]) : null,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
          paramDateFilterRange[1] ? new Date(paramDateFilterRange[1]) : null,
        ]
      : [null, null],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    orderType: params.get('orderType')
      ? JSON.parse(params.get('orderType') as string)
      : null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    active: params.get('active')
      ? JSON.parse(params.get('active') as string)
      : null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    abandonedAfterBuy: params.get('abandonedAfterBuy')
      ? JSON.parse(params.get('abandonedAfterBuy') as string)
      : null,
    statusFilter: params.get('statusFilter')
      ? STATUS_FILTER_OPTIONS.filter((o) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call
          JSON.parse(params.get('statusFilter') as string)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .map((v: Option) => v.value)
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            .includes(o.value)
        )
      : [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    sortOptions: params.get('sortOptions')
      ? JSON.parse(params.get('sortOptions') as string)
      : undefined,
    searchInput: params.get('searchInput') || '',
    searchByReferenceIdInput: params.get('searchByReferenceIdInput') || '',
    searchByUserIdInput: params.get('searchByUserIdInput') || '',
    searchByNexusTxCodeInput: params.get('searchByNexusTxCodeInput') || '',
    searchQuery: params.get('searchQuery') || '',
    searchByReferenceIdQuery: params.get('searchByReferenceIdQuery') || '',
    searchByUserIdQuery: params.get('searchByUserIdQuery') || '',
    searchByNexusTxCodeQuery: params.get('searchByNexusTxCodeQuery') || '',
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    merchant: params.get('merchant')
      ? JSON.parse(params.get('merchant') as string)
      : [],
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    errorFilter: params.get('errorFilter')
      ? JSON.parse(params.get('errorFilter') as string)
      : null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    hasFailedCallback: params.get('hasFailedCallback')
      ? JSON.parse(params.get('hasFailedCallback') as string)
      : null,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    platform: params.get('platform')
      ? JSON.parse(params.get('platform') as string)
      : null,
  };
};
export const MerchantOrders = () => {
  const session = useSession();

  const canStartProcessingFlow =
    session.authenticated &&
    session.permissions.includes('edit_merchant_orders') &&
    process.env.REACT_APP_ENVIRONMENT !== 'production';

  const { spacing, palette, sizing } = useTheme();
  const { useGetMerchantOrders } = useMerchantOrdersApi();
  // State for filters persisted in query params
  const [params, setParams] = useSearchParams();
  const [searchByReferenceIdInput, setSearchByReferenceIdInput] = useState(
    params.get('searchByReferenceIdQuery') || ''
  );
  const [searchByUserIdInput, setSearchByUserIdInput] = useState(
    params.get('searchByUserIdQuery') || ''
  );
  const [searchByNexusTxCodeInput, setSearchByNexusTxCodeInput] = useState(
    params.get('searchByNexusTxCodeQuery') || ''
  );

  const [searchInput, setSearchInput] = useState(
    params.get('searchQuery') || ''
  );

  const [filterState, setFilterState] = useState<MerchantOrdersUiFilters>(
    getInitialFilterState(params)
  );
  const {
    page,
    dateFilterRange,
    sortOptions,
    orderType,
    active,
    statusFilter,
    searchQuery,
    searchByReferenceIdQuery,
    searchByUserIdQuery,
    searchByNexusTxCodeQuery,
    merchant,
    errorFilter,
    abandonedAfterBuy,
    hasFailedCallback,
    platform,
  } = filterState;
  const setFilterStateFor = <K extends keyof MerchantOrdersUiFilters>(
    filterName: K,
    isObject: boolean
  ) => {
    return (newValue: MerchantOrdersUiFilters[K]) => {
      params.set(
        filterName,
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        isObject ? JSON.stringify(newValue) : String(newValue)
      );
      setParams(params);
      setFilterState((state) => ({ ...state, [filterName]: newValue }));
    };
  };
  const setPage = setFilterStateFor('page', false);
  const setDateFilterRange = setFilterStateFor('dateFilterRange', true);
  const setSortOptions = setFilterStateFor('sortOptions', true);
  const setOrderType = setFilterStateFor('orderType', true);
  const setActive = setFilterStateFor('active', true);
  const setStatusFilter = setFilterStateFor('statusFilter', true);
  const setSearchQuery = setFilterStateFor('searchQuery', false);
  const setSearchByReferenceIdQuery = setFilterStateFor(
    'searchByReferenceIdQuery',
    false
  );
  const setSearchByNexusTxCodeQuery = setFilterStateFor(
    'searchByNexusTxCodeQuery',
    false
  );
  const setSearchByUserIdQuery = setFilterStateFor(
    'searchByUserIdQuery',
    false
  );
  const setMerchant = setFilterStateFor('merchant', true);
  const setPlatform = setFilterStateFor('platform', true);
  const setErrorFilter = setFilterStateFor('errorFilter', true);
  const setAbandonedAfterBuy = setFilterStateFor('abandonedAfterBuy', true);
  const setHasFailedCallback = setFilterStateFor('hasFailedCallback', true);

  const [selectedOrder, setSelectedOrder] = useState<OrderMinimal>();
  const [batchSelected, setBatchSelected] = useState<Set<string>>(new Set());

  const handleSort = useCallback(
    (cellKey: string, asc: boolean) => {
      setBatchSelected(new Set());
      setSortOptions({
        field: CELL_KEY_TO_SORT_FIELD[cellKey as CellKey],
        ascending: asc,
      });
    },
    [setSortOptions]
  );

  const handleResetFilters = () => {
    setParams(undefined);
    setFilterState(initialFilterState);
    setBatchSelected(new Set());
  };

  const filters = {
    createdFrom: dateFilterRange[0],
    createdTo: dateFilterRange[1],
    statuses: statusFilter.map((v) => v.value) as OrderStatus[],
    orderOrPreviewId: searchQuery,
    referenceId: searchByReferenceIdQuery,
    userId: params.get('searchByUserIdQuery') || searchByUserIdQuery,
    nexusTxCode:
      params.get('searchByNexusTxCodeQuery') || searchByNexusTxCodeQuery,
    merchantIds: merchant.map((m) => m.value),
    orderType: orderType?.value as OrderType,
    active: active ? active.value === 'Active' : null,
    errors: errorFilter ? errorFilter.value === 'With errors' : null,
    abandonedAfterBuy: abandonedAfterBuy ? true : null,
    hasFailedCallback: hasFailedCallback ? true : null,
  };
  // Because default createdFrom we want to ignore it when searching for ids
  if (
    dateFilterRange[0]?.getTime() ==
      initialFilterState.dateFilterRange[0]?.getTime() &&
    (filters.userId ||
      filters.merchantIds.length ||
      filters.referenceId ||
      filters.orderOrPreviewId)
  ) {
    filters.createdFrom = null;
  }

  const {
    data: value,
    isFetching,
    refetch: refreshOrders,
  } = useGetMerchantOrders(filters, { size: PAGE_SIZE, page }, sortOptions);

  const updateQueryParam = (
    key: string,
    input: string,
    setQuery: (value: string) => void
  ) => {
    setQuery(input);
    if (input === '') {
      params.delete(key);
      setParams(params);
    } else {
      setPage(1);
    }
  };

  useDebounce(
    () => {
      if (searchInput !== searchQuery) {
        updateQueryParam('searchQuery', searchInput, setSearchQuery);
      }
      if (searchByReferenceIdInput !== searchByReferenceIdQuery) {
        updateQueryParam(
          'searchByReferenceIdQuery',
          searchByReferenceIdInput,
          setSearchByReferenceIdQuery
        );
      }
      if (searchByUserIdInput !== searchByUserIdQuery) {
        updateQueryParam(
          'searchByUserIdQuery',
          searchByUserIdInput,
          setSearchByUserIdQuery
        );
      }
      if (searchByNexusTxCodeInput !== searchByNexusTxCodeQuery) {
        updateQueryParam(
          'searchByNexusTxCodeQuery',
          searchByNexusTxCodeInput,
          setSearchByNexusTxCodeQuery
        );
      }
      if (
        !searchInput &&
        !searchByReferenceIdInput &&
        !searchByUserIdInput &&
        !searchByNexusTxCodeInput
      ) {
        params.delete('page');
        setParams(params);
      }
    },
    500,
    [
      searchInput,
      searchByReferenceIdInput,
      searchByUserIdInput,
      searchByNexusTxCodeInput,
    ]
  );

  const headers = getHeaders();
  const rows = useMemo(
    () =>
      value?.orders.map((order) => ({
        id: order.uuid,
        value: {
          createdAt: order.createdAt.toLocaleString(),
          completedAt: order.completedAt?.toLocaleString(),
          orderId: (
            <div>
              <TruncatedString text={order.uuid} />
              {order.referenceId && (
                <Typography variant="bodySmallest">
                  Reference ID: {order.referenceId}
                </Typography>
              )}
            </div>
          ),
          amount: `${order.amount} ${order.currency}`,
          status: order.status,
          merchantName: (
            <div css={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="bodySmall">{order.merchant.name}</Typography>
              <Copy textToCopy={order.merchant.uuid} />
            </div>
          ),
          details: (
            <div
              css={{ display: 'flex', alignItems: 'center', gap: spacing.xxxs }}
            >
              <UtilityButton
                variant="text"
                onClick={() => setSelectedOrder(order)}
                text="View details"
              />
              {order.hasErrors && <Icon name="error" />}
            </div>
          ),
        },
      })),
    [value, spacing]
  );

  if (!value) return <QBLoader />;
  return (
    <>
      <div
        css={{
          display: 'grid',
          gridTemplateRows: 'auto auto auto 1fr auto',
          height: '100%',
        }}
      >
        <Header>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="subtitle">Orders</Typography>
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: canStartProcessingFlow
                  ? 'auto auto 1fr 1fr'
                  : 'auto 1fr 1fr',
                alignItems: 'center',
              }}
            >
              {canStartProcessingFlow && (
                <CreateProcessingOrders onCreate={refreshOrders} />
              )}
              <ExportCsv
                filters={{
                  createdFrom: dateFilterRange[0],
                  createdTo: dateFilterRange[1],
                  statuses: statusFilter.map((v) => v.value) as OrderStatus[],
                  orderOrPreviewId: searchQuery,
                  referenceId: searchByReferenceIdQuery,
                  userId: searchByUserIdQuery,
                  nexusTxCode: searchByNexusTxCodeQuery,
                  merchantIds: merchant.map((m) => m.value),
                  orderType: orderType?.value as OrderType,
                  active: active ? active.value === 'Active' : null,
                  errors: errorFilter
                    ? errorFilter.value === 'With errors'
                    : null,
                  abandonedAfterBuy: abandonedAfterBuy ? true : null,
                  hasFailedCallback: hasFailedCallback ? true : null,
                }}
                sortOptions={sortOptions}
              />
            </div>
          </div>
        </Header>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: 'auto auto auto auto auto',
            gap: spacing.xxxs,
            alignItems: 'center',
            zIndex: 1,
          }}
        >
          <SearchInput
            placeholder="Order ID or Receive Preview ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <SearchInput
            placeholder="Reference ID"
            value={searchByReferenceIdInput}
            onChange={(e) => setSearchByReferenceIdInput(e.target.value)}
          />
          <SearchInput
            placeholder="User ID"
            value={searchByUserIdInput}
            onChange={(e) => setSearchByUserIdInput(e.target.value)}
          />
          <SearchInput
            placeholder="Nexus Tx Code"
            value={searchByNexusTxCodeInput}
            onChange={(e) => setSearchByNexusTxCodeInput(e.target.value)}
          />
        </div>
        <div
          css={{
            marginTop: spacing.xxs,
            display: 'grid',
            gridTemplateColumns: '8fr 1fr',
            textWrap: 'nowrap',
          }}
        >
          <div
            css={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: spacing.xxxs,
              alignItems: 'center',
              zIndex: 1,
            }}
          >
            <DateInput
              placeholder="All time"
              type="range"
              value={dateFilterRange}
              onChange={(e: [Date | null, Date | null]) => {
                setPage(1);
                setDateFilterRange(e);
              }}
              withTime
            />
            <UtilitySelect
              label={
                statusFilter.length > 0
                  ? `Statuses (${statusFilter.length})`
                  : 'All statuses'
              }
              listTitle="Status"
              options={STATUS_FILTER_OPTIONS}
              value={statusFilter}
              multiple={true}
              onSelect={(data) => {
                setPage(1);
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                setStatusFilter((data as Option[]) || []);
              }}
            />
            <UtilitySelect
              label={orderType ? _.capitalize(orderType.value) : 'Order type'}
              listTitle="Order type"
              options={ORDER_TYPE_FILTER_OPTIONS}
              value={orderType}
              onSelect={(data) => {
                setPage(1);
                setOrderType(data as Option);
              }}
            />
            <UtilitySelect
              label={
                active === null
                  ? 'Active & Inactive'
                  : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  active
                  ? 'Only Active'
                  : 'Only Inactive'
              }
              listTitle="Order activity"
              options={ORDER_ACTIVITY_FILTER_OPTIONS}
              value={active}
              onSelect={(data) => {
                setPage(1);
                setActive(data as Option);
              }}
            />
            <UtilitySelect
              label={errorFilter === null ? 'Errors' : errorFilter.label}
              listTitle="Order errors"
              options={ERROR_FILTER_OPTIONS}
              value={errorFilter}
              onSelect={(data) => {
                setPage(1);
                setErrorFilter(data as Option);
              }}
            />
            <UtilitySelect
              label={
                abandonedAfterBuy === null
                  ? 'Abandoned'
                  : abandonedAfterBuy.label
              }
              listTitle="Abandoned orders"
              options={ORDER_ABANDONED_FILTER_OPTIONS}
              value={abandonedAfterBuy}
              onSelect={(data) => {
                setPage(1);
                setAbandonedAfterBuy(data as Option);
              }}
            />
            <UtilitySelect
              label={
                hasFailedCallback === null
                  ? 'Failed callback'
                  : hasFailedCallback.label
              }
              listTitle="Failed callback"
              options={HAS_FAILED_CALLBACK_OPTIONS}
              value={hasFailedCallback}
              onSelect={(data) => {
                setPage(1);
                setHasFailedCallback(data as Option);
              }}
            />
            <MerchantSelect
              merchant={merchant}
              setMerchant={(m) => {
                setPage(1);
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                setMerchant((m as Option[]) || []);
              }}
              searchable={true}
              multiple={true}
            />
            <PlatformSelect
              platform={platform}
              setPlatform={(p) => {
                setPage(1);
                setPlatform(p as Option);
                if (p?.merchants) {
                  setMerchant(p.merchants);
                }
              }}
            />
            <UtilityButton
              variant="text"
              text="Reset filters"
              onClick={handleResetFilters}
            />
            <UtilityButton
              variant="secondary"
              text="Refresh"
              onClick={() => refreshOrders()}
              disabled={isFetching}
            />
            {isFetching && (
              <Icon
                name="spinner"
                className={{ svg: { color: palette.grey.default } }}
              />
            )}
          </div>
          <div
            css={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'end',
            }}
          >
            {batchSelected.size > 0 && (
              <div>
                <UtilityButton
                  text="Batch Actions"
                  dropdown={
                    <Card css={{ minWidth: sizing.medium }}>
                      <RetryActions
                        orders={value.orders.filter((o) =>
                          batchSelected.has(o.uuid)
                        )}
                      />
                    </Card>
                  }
                />
              </div>
            )}
          </div>
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Table
            headers={headers}
            rows={rows ?? []}
            onSort={handleSort}
            selected={batchSelected}
            updateSelected={setBatchSelected}
          />
        </div>
        <div css={{ marginTop: spacing.xxs }}>
          <Pagination
            currentPage={page}
            onChange={setPage}
            numberOfElements={value.aggregatedData.orderCount}
            pageSize={PAGE_SIZE}
          />
        </div>
      </div>
      {selectedOrder && (
        <OrderDetails
          minimalOrder={selectedOrder}
          deselect={() => setSelectedOrder(undefined)}
        />
      )}
    </>
  );
};
